import { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Breadcrumb } from '../components/Breadcrumb';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { format } from 'date-fns';
import { STAFF_ROLES } from '../utils/permissions';

interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface GetCurrentUserData {
  getCurrentUser: CurrentUser;
}

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface ListStaffData {
  listStaff: StaffMember[];
}

interface ListAllClientsContactStatusData {
  listAllClientsContactStatus: {
    items: ContactTrackingRecord[];
    nextToken?: string;
  };
}

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

const LIST_ALL_CLIENTS_CONTACT_STATUS = gql`
  query ListAllClientsContactStatus(
    $limit: Int
    $nextToken: String
    $filter: ContactStatusFilter
  ) {
    listAllClientsContactStatus(
      limit: $limit
      nextToken: $nextToken
      filter: $filter
    ) {
      items {
        clientId
        clientName
        caseId
        caseNumber
        lastContactDate
        daysSinceContact
        assignedAttorneyId
        assignedAttorneyName
        assignedCaseManagerId
        assignedCaseManagerName
        status
      }
      nextToken
    }
  }
`;

const LIST_STAFF = gql`
  query ListStaff {
    listStaff {
      id
      firstName
      lastName
      role
    }
  }
`;

type SortField = 'clientName' | 'caseNumber' | 'lastContactDate' | 'daysSinceContact' | 'status';
type SortDirection = 'asc' | 'desc';
type ContactStatus = 'CURRENT' | 'APPROACHING' | 'OVERDUE';

interface ContactTrackingRecord {
  clientId: string;
  clientName: string;
  caseId: string;
  caseNumber: string;
  lastContactDate: string;
  daysSinceContact: number;
  assignedAttorneyId?: string;
  assignedAttorneyName?: string;
  assignedCaseManagerId?: string;
  assignedCaseManagerName?: string;
  status: ContactStatus;
}

export function ContactTrackingDashboard() {
  const navigate = useNavigate();
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [staffFilter, setStaffFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<ContactStatus | 'all'>('all');
  
  // Table controls
  const [sortField, setSortField] = useState<SortField>('daysSinceContact');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const { data: userData, loading: userLoading } = useQuery<GetCurrentUserData>(GET_CURRENT_USER);
  const { data: staffData } = useQuery<ListStaffData>(LIST_STAFF);
  
  const user = userData?.getCurrentUser;
  const staffMembers = staffData?.listStaff || [];

  // Build filter for GraphQL query
  const graphqlFilter = useMemo(() => {
    const filter: any = {};
    if (staffFilter !== 'all') {
      filter.staffId = staffFilter;
    }
    if (statusFilter !== 'all') {
      filter.status = statusFilter;
    }
    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [staffFilter, statusFilter]);

  const { data: contactData, loading: contactLoading, refetch } = useQuery<ListAllClientsContactStatusData>(
    LIST_ALL_CLIENTS_CONTACT_STATUS,
    {
      variables: {
        limit: 1000, // Get all for client-side filtering/sorting
        filter: graphqlFilter,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const allClients: ContactTrackingRecord[] = contactData?.listAllClientsContactStatus?.items || [];

  // Client-side filtering, sorting, and pagination
  const { paginatedClients, totalPages, totalFiltered, stats } = useMemo(() => {
    let filtered = allClients.filter((client) => {
      // Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          client.clientName?.toLowerCase().includes(query) ||
          client.caseNumber?.toLowerCase().includes(query) ||
          client.assignedAttorneyName?.toLowerCase().includes(query) ||
          client.assignedCaseManagerName?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      let aVal: any, bVal: any;
      
      switch (sortField) {
        case 'clientName':
          aVal = a.clientName?.toLowerCase() || '';
          bVal = b.clientName?.toLowerCase() || '';
          break;
        case 'caseNumber':
          aVal = a.caseNumber?.toLowerCase() || '';
          bVal = b.caseNumber?.toLowerCase() || '';
          break;
        case 'lastContactDate':
          aVal = new Date(a.lastContactDate).getTime();
          bVal = new Date(b.lastContactDate).getTime();
          break;
        case 'daysSinceContact':
          aVal = a.daysSinceContact;
          bVal = b.daysSinceContact;
          break;
        case 'status':
          const statusOrder = { CURRENT: 0, APPROACHING: 1, OVERDUE: 2 };
          aVal = statusOrder[a.status];
          bVal = statusOrder[b.status];
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Calculate stats
    const currentCount = filtered.filter(c => c.status === 'CURRENT').length;
    const approachingCount = filtered.filter(c => c.status === 'APPROACHING').length;
    const overdueCount = filtered.filter(c => c.status === 'OVERDUE').length;

    // Pagination
    const totalFiltered = filtered.length;
    const totalPages = Math.ceil(totalFiltered / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedClients = filtered.slice(startIndex, endIndex);

    return {
      paginatedClients,
      totalPages,
      totalFiltered,
      stats: {
        total: allClients.length,
        current: currentCount,
        approaching: approachingCount,
        overdue: overdueCount,
      },
    };
  }, [allClients, searchQuery, sortField, sortDirection, currentPage, pageSize]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (setter: any) => (value: any) => {
    setter(value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStaffFilter('all');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handleExport = () => {
    const csvContent = [
      ['Client Name', 'Case Number', 'Attorney', 'Case Manager', 'Last Contact', 'Days Since Contact', 'Status'].join(','),
      ...allClients.map((c) => [
        `"${c.clientName}"`,
        c.caseNumber || '',
        c.assignedAttorneyName || '',
        c.assignedCaseManagerName || '',
        format(new Date(c.lastContactDate), 'yyyy-MM-dd'),
        c.daysSinceContact,
        c.status,
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contact-tracking-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status: ContactStatus) => {
    switch (status) {
      case 'CURRENT':
        return <Badge variant="success">Current</Badge>;
      case 'APPROACHING':
        return <Badge variant="warning">Approaching</Badge>;
      case 'OVERDUE':
        return <Badge variant="destructive">Overdue</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (userLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red"></div>
      </div>
    );
  }

  // Check if user is staff (all staff can access)
  const isStaff = user?.role && STAFF_ROLES.includes(user.role as any);
  
  if (!user || !isStaff) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={user} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
              <p className="text-gray-600 mb-4">This dashboard is only available to staff members.</p>
              <Button onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb />

        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 sticky top-16 z-40">
          <div className="max-w-full px-6">
            <div className="flex items-center justify-between h-14">
              <div className="flex items-center gap-4">
                <h1 className="text-lg font-semibold text-aalaw-charcoal">Contact Tracking Dashboard</h1>
                <Badge variant="secondary" className="text-xs">
                  {totalFiltered.toLocaleString()} {totalFiltered === 1 ? 'client' : 'clients'}
                  {totalFiltered !== allClients.length && ` of ${allClients.length.toLocaleString()} total`}
                </Badge>
                {totalFiltered > 0 && (
                  <span className="text-xs text-gray-500">
                    Page {currentPage} of {totalPages}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Page Size */}
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="h-8 px-2 text-xs border border-gray-300 rounded-lg"
                >
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                
                <Button variant="outline" size="sm" onClick={handleExport}>
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-full px-6 py-6">
          {/* Stats Bar */}
          <div className="grid grid-cols-4 gap-4 mb-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stats.total.toLocaleString()}</p>
                    <p className="text-sm text-gray-600">Total Clients</p>
                  </div>
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-green-600">{stats.current.toLocaleString()}</p>
                    <p className="text-sm text-gray-600">Current (&lt;21 days)</p>
                  </div>
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-yellow-600">{stats.approaching.toLocaleString()}</p>
                    <p className="text-sm text-gray-600">Approaching (21-27 days)</p>
                  </div>
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-red-600">{stats.overdue.toLocaleString()}</p>
                    <p className="text-sm text-gray-600">Overdue (≥28 days)</p>
                  </div>
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters Bar */}
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="grid grid-cols-12 gap-3">
                {/* Search */}
                <div className="col-span-4">
                  <Input
                    type="text"
                    placeholder="Search by client name, case number, or staff..."
                    value={searchQuery}
                    onChange={(e) => handleFilterChange(setSearchQuery)(e.target.value)}
                    className="w-full h-9 text-sm"
                  />
                </div>

                {/* Staff Filter */}
                <div className="col-span-3">
                  <select
                    value={staffFilter}
                    onChange={(e) => handleFilterChange(setStaffFilter)(e.target.value)}
                    className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                  >
                    <option value="all">All Staff</option>
                    {staffMembers.map((staff: any) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.firstName} {staff.lastName} ({staff.role})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status Filter */}
                <div className="col-span-3">
                  <select
                    value={statusFilter}
                    onChange={(e) => handleFilterChange(setStatusFilter)(e.target.value as any)}
                    className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                  >
                    <option value="all">All Statuses</option>
                    <option value="CURRENT">Current (&lt;21 days)</option>
                    <option value="APPROACHING">Approaching (21-27 days)</option>
                    <option value="OVERDUE">Overdue (≥28 days)</option>
                  </select>
                </div>

                {/* Actions */}
                <div className="col-span-2 flex items-center justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => refetch()}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </Button>
                  <button
                    onClick={clearFilters}
                    className="text-sm text-gray-600 hover:text-aalaw-red"
                    title="Clear all filters"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3">
                      <button
                        onClick={() => handleSort('clientName')}
                        className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                      >
                        Client Name
                        <SortIcon field="clientName" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3">
                      <button
                        onClick={() => handleSort('caseNumber')}
                        className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                      >
                        Case Number
                        <SortIcon field="caseNumber" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                      Assigned Attorney
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                      Case Manager
                    </th>
                    <th className="text-left px-4 py-3">
                      <button
                        onClick={() => handleSort('lastContactDate')}
                        className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                      >
                        Last Contact
                        <SortIcon field="lastContactDate" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3">
                      <button
                        onClick={() => handleSort('daysSinceContact')}
                        className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                      >
                        Days Since
                        <SortIcon field="daysSinceContact" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3">
                      <button
                        onClick={() => handleSort('status')}
                        className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                      >
                        Status
                        <SortIcon field="status" />
                      </button>
                    </th>
                    <th className="w-24 px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {contactLoading && !contactData ? (
                    <tr>
                      <td colSpan={8} className="text-center py-12">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedClients.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-12">
                        <p className="text-gray-500">No clients found</p>
                        <p className="text-gray-400 text-sm mt-1">
                          {searchQuery || staffFilter !== 'all' || statusFilter !== 'all'
                            ? 'Try adjusting your filters'
                            : 'Contact tracking data will appear here'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    paginatedClients.map((client) => (
                      <tr
                        key={client.clientId}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/clients/${client.clientId}`)}
                      >
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-gray-900">{client.clientName}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-700">{client.caseNumber || '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-700">{client.assignedAttorneyName || '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-700">{client.assignedCaseManagerName || '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600">
                            {format(new Date(client.lastContactDate), 'MMM d, yyyy')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm font-medium ${
                            client.daysSinceContact >= 28 ? 'text-red-600' :
                            client.daysSinceContact >= 21 ? 'text-yellow-600' :
                            'text-green-600'
                          }`}>
                            {client.daysSinceContact} days
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(client.status)}
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/clients/${client.clientId}`)}
                            className="text-aalaw-red hover:text-aalaw-red-dark hover:bg-red-50"
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-600">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalFiltered)} of {totalFiltered} results
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {getPageNumbers().map((page, index) => (
                  typeof page === 'number' ? (
                    <button
                      key={index}
                      onClick={() => goToPage(page)}
                      className={`px-3 py-1 text-sm rounded-md ${
                        currentPage === page
                          ? 'bg-aalaw-red text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {page}
                    </button>
                  ) : (
                    <span key={index} className="px-2 text-gray-400">
                      {page}
                    </span>
                  )
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
